// Full-screen image viewer with pinch-zoom, pan, +/- controls, and gesture hint (PWA-safe; no viewport change).

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslationSafe } from '../../contexts/LanguageContext';
import { usePinchZoomPan } from '../../hooks/usePinchZoomPan';
import { HINT_TOOLTIP_SURFACE_CLASS } from './PressableHint';

const HINT_AUTO_HIDE_MS = 5000;

type FullscreenPinchImageViewerProps = {
  open: boolean;
  onClose: () => void;
  src: string;
  alt: string;
  dialogAriaLabel: string;
  closeLabel: string;
};

export const FullscreenPinchImageViewer = ({
  open,
  onClose,
  src,
  alt,
  dialogAriaLabel,
  closeLabel,
}: FullscreenPinchImageViewerProps) => {
  const t = useTranslationSafe();
  const hintId = useId();
  const gestureRef = useRef<HTMLDivElement>(null);
  const [hintVisible, setHintVisible] = useState(false);

  const {
    scale,
    translateX,
    translateY,
    reset,
    zoomIn,
    zoomOut,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleDoubleClick,
    handleWheel,
  } = usePinchZoomPan(open, gestureRef);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      setHintVisible(false);
      return undefined;
    }
    setHintVisible(true);
    const timer = window.setTimeout(() => {
      setHintVisible(false);
    }, HINT_AUTO_HIDE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [open]);

  const handleBackdropKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  const handleZoomInKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        zoomIn();
      }
    },
    [zoomIn],
  );

  const handleZoomOutKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        zoomOut();
      }
    },
    [zoomOut],
  );

  const handleResetKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        reset();
      }
    },
    [reset],
  );

  if (!open) {
    return null;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const scalePercent = Math.round(scale * 100);
  const transformStyle = {
    transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
  };
  const panCursorClass = scale > 1 ? 'cursor-grab active:cursor-grabbing' : '';

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex flex-col bg-black/85 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      role="dialog"
      aria-modal="true"
      aria-label={dialogAriaLabel}
      tabIndex={-1}
      onClick={onClose}
      onKeyDown={handleBackdropKeyDown}
    >
      <div className="relative mx-auto flex w-full max-w-4xl shrink-0 justify-end">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          className="min-h-11 rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white"
          tabIndex={0}
        >
          {closeLabel}
        </button>
      </div>

      {hintVisible ? (
        <p
          id={hintId}
          role="status"
          aria-live="polite"
          className={`pointer-events-none relative z-10 mx-auto mt-2 max-w-sm text-center ${HINT_TOOLTIP_SURFACE_CLASS} whitespace-normal`}
          onClick={(event) => event.stopPropagation()}
        >
          {t('common.imageViewer.zoomHint')}
        </p>
      ) : null}

      <div
        ref={gestureRef}
        className={`relative mx-auto flex min-h-0 w-full max-w-4xl flex-1 touch-none select-none items-center justify-center overflow-hidden ${panCursorClass}`}
        style={{ touchAction: 'none' }}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        tabIndex={0}
        aria-label={dialogAriaLabel}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-h-[calc(100dvh-10rem)] max-w-full object-contain transition-none"
          style={transformStyle}
        />
      </div>

      <div
        className="mx-auto mt-3 flex w-full max-w-4xl shrink-0 items-center justify-center gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={zoomOut}
          onKeyDown={handleZoomOutKeyDown}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-white/15 text-xl font-medium text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-40"
          aria-label={t('common.imageViewer.zoomOutAria')}
          disabled={scale <= 1}
          tabIndex={0}
        >
          −
        </button>
        <button
          type="button"
          onClick={reset}
          onKeyDown={handleResetKeyDown}
          className="min-h-11 min-w-[4.5rem] rounded-lg bg-white/10 px-3 text-sm font-semibold tabular-nums text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={t('common.imageViewer.resetZoomAria')}
          tabIndex={0}
        >
          {t('common.imageViewer.scalePercent', { percent: scalePercent })}
        </button>
        <button
          type="button"
          onClick={zoomIn}
          onKeyDown={handleZoomInKeyDown}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-white/15 text-xl font-medium text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-40"
          aria-label={t('common.imageViewer.zoomInAria')}
          disabled={scale >= 4}
          tabIndex={0}
        >
          +
        </button>
      </div>
    </div>,
    document.body,
  );
};
